/** WORKERS-COMPATIBLE: No DOM/browser APIs. Runs in Cloudflare Workers. */

export interface ReportData {
  reportType: 'campaign' | 'advertiser'
  orgName: string
  subtitle: string
  contextFields: Array<{ label: string; value: string }>
  metrics: Array<{ label: string; value: string; sub?: string }>
  chartData?: Array<{ label: string; opens: number; clicks: number }>
  tableTitle?: string
  tableHeaders?: string[]
  tableRows?: Array<string[]>
  totalRow?: string[]
  generatedAt: string
}

function esc(v: string | number): string {
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const C = {
  brand: '#FF6B00',
  dark: '#111827',
  gray: '#6B7280',
  lightGray: '#F3F4F6',
  border: '#E5E7EB',
  white: '#FFFFFF',
  green: '#10B981',
}

const STAMP = `<svg width="36" height="36" viewBox="0 0 36 36"><rect width="36" height="36" rx="6" fill="${C.brand}"/><rect x="4" y="4" width="8" height="6" rx="1" fill="#fff" opacity="0.3"/><path d="M5 19L18 28L31 19" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="5" y1="12" x2="18" y2="12" stroke="#fff" stroke-width="1.2" stroke-linecap="round" opacity="0.4"/><line x1="5" y1="15" x2="13" y2="15" stroke="#fff" stroke-width="1" stroke-linecap="round" opacity="0.25"/></svg>`

export function buildSvgChart(
  data: Array<{ label: string; opens: number; clicks: number }>,
): string {
  if (!data.length) return ''
  const W = 700,
    H = 220,
    P = { t: 25, r: 20, b: 40, l: 50 }
  const pw = W - P.l - P.r,
    ph = H - P.t - P.b
  const maxV = Math.max(...data.flatMap((d) => [d.opens, d.clicks]), 1)
  const nice = Math.ceil(maxV / 5) * 5 || 5
  const n = data.length,
    barW = Math.min(28, (pw / n) * 0.35)
  function x(i: number): number {
    return P.l + ((i + 0.5) / n) * pw
  }
  function y(v: number): number {
    return P.t + ph - (v / nice) * ph
  }
  const grid = Array.from({ length: 6 }, (_, i) => {
    const val = Math.round((nice / 5) * i),
      yp = y(val)
    return `<line x1="${P.l}" y1="${yp}" x2="${W - P.r}" y2="${yp}" stroke="${C.border}" stroke-width="0.5"/><text x="${P.l - 8}" y="${yp + 3}" text-anchor="end" font-size="9" fill="${C.gray}">${val}</text>`
  }).join('')
  const bars = data
    .map(
      (d, i) =>
        `<rect x="${x(i) - barW / 2}" y="${y(d.clicks)}" width="${barW}" height="${Math.max(0, y(0) - y(d.clicks))}" rx="2" fill="${C.green}" opacity="0.65"/>`,
    )
    .join('')
  const pts = data.map((d, i) => `${x(i)},${y(d.opens)}`).join(' ')
  const area = `${pts} ${x(n - 1)},${y(0)} ${x(0)},${y(0)}`
  const step = Math.max(1, Math.floor(n / 8))
  const labels = data
    .map((d, i) =>
      i % step === 0 || i === n - 1
        ? `<text x="${x(i)}" y="${H - 8}" text-anchor="middle" font-size="9" fill="${C.gray}">${esc(d.label)}</text>`
        : '',
    )
    .filter(Boolean)
    .join('')
  const dots = data
    .map(
      (d, i) =>
        `<circle cx="${x(i)}" cy="${y(d.opens)}" r="3.5" fill="${C.white}" stroke="${C.brand}" stroke-width="2"/>`,
    )
    .join('')
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
<rect width="${W}" height="${H}" rx="6" fill="${C.white}" stroke="${C.border}" stroke-width="1"/>
${grid}${bars}<polygon points="${area}" fill="rgba(255,107,0,0.08)"/>
<polyline points="${pts}" fill="none" stroke="${C.brand}" stroke-width="2.5" stroke-linejoin="round"/>${dots}${labels}
<line x1="${W - 160}" y1="14" x2="${W - 148}" y2="14" stroke="${C.brand}" stroke-width="2.5"/>
<circle cx="${W - 154}" cy="14" r="3" fill="${C.white}" stroke="${C.brand}" stroke-width="1.5"/>
<text x="${W - 142}" y="17" font-size="10" fill="${C.dark}">Opens</text>
<rect x="${W - 80}" y="9" width="10" height="10" rx="2" fill="${C.green}" opacity="0.65"/>
<text x="${W - 66}" y="17" font-size="10" fill="${C.dark}">Clicks</text></svg>`
}

function metricCard(label: string, value: string, sub?: string): string {
  return `<td style="width:25%;padding:6px;"><table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid ${C.border};border-radius:8px;border-left:3px solid ${C.brand};background:${C.white};">
<tr><td style="padding:12px 14px 4px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;color:${C.gray};">${esc(label)}</td></tr>
<tr><td style="padding:2px 14px 12px;"><span style="font-size:26px;font-weight:700;color:${C.dark};">${esc(value)}</span>${sub ? `<span style="font-size:12px;color:${C.gray};margin-left:6px;">${esc(sub)}</span>` : ''}</td></tr>
</table></td>`
}

function thCell(text: string, align?: string): string {
  return `<th style="padding:10px 12px;text-align:${align ?? 'left'};font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;color:${C.brand};border-bottom:2px solid ${C.border};">${esc(text)}</th>`
}

function tdCell(text: string, align?: string, bold?: boolean): string {
  return `<td style="padding:10px 12px;font-size:12px;color:${C.dark};border-bottom:1px solid ${C.border};text-align:${align ?? 'left'};${bold ? 'font-weight:700;' : ''}">${esc(text)}</td>`
}

export function buildReportHtml(data: ReportData): string {
  const metrics = `<table width="100%" cellpadding="0" cellspacing="0"><tr>${data.metrics.map((m) => metricCard(m.label, m.value, m.sub)).join('')}</tr></table>`
  const chart = data.chartData?.length ? buildSvgChart(data.chartData) : ''
  const ctx = data.contextFields
    .map(
      (f) =>
        `<td style="padding:12px 16px;"><div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;color:${C.gray};margin-bottom:2px;">${esc(f.label)}</div><div style="font-size:14px;font-weight:600;color:${C.dark};">${esc(f.value)}</div></td>`,
    )
    .join('')
  let tableHtml = ''
  if (data.tableHeaders?.length && data.tableRows?.length) {
    const hdr = data.tableHeaders.map((h, i) => thCell(h, i >= 2 ? 'right' : 'left')).join('')
    const rows = data.tableRows
      .map((row) => `<tr>${row.map((c, i) => tdCell(c, i >= 2 ? 'right' : 'left')).join('')}</tr>`)
      .join('')
    const total = data.totalRow
      ? `<tr style="background:${C.lightGray};">${data.totalRow.map((c, i) => tdCell(c, i >= 2 ? 'right' : 'left', true)).join('')}</tr>`
      : ''
    tableHtml = `<table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid ${C.border};border-radius:8px;overflow:hidden;"><tr>${hdr}</tr>${rows}${total}</table>`
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:;">
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Roboto,-apple-system,sans-serif;width:794px;background:${C.white};color:${C.dark};}</style></head>
<body><table width="794" cellpadding="0" cellspacing="0">
<tr><td style="height:4px;background:${C.brand};"></td></tr>
<tr><td style="padding:28px 32px 20px;"><table width="100%"><tr>
<td style="vertical-align:top;">${STAMP}<div style="margin-top:12px;"><div style="font-size:26px;font-weight:700;color:${C.dark};line-height:1.2;">${esc(data.orgName)}</div>
<div style="font-size:14px;color:${C.gray};margin-top:2px;">${esc(data.subtitle)}</div></div></td>
<td style="text-align:right;vertical-align:top;"><div style="font-size:13px;color:${C.brand};font-weight:600;">one-email.com</div>
<div style="font-size:12px;color:${C.gray};margin-top:2px;">Generated ${esc(data.generatedAt)}</div></td>
</tr></table></td></tr>
<tr><td style="padding:0 32px;"><table width="100%" cellpadding="0" cellspacing="0" style="background:${C.lightGray};border-radius:8px;"><tr>${ctx}</tr></table></td></tr>
<tr><td style="padding:20px 32px 0;">${metrics}</td></tr>
${chart ? `<tr><td style="padding:24px 32px 0;"><div style="font-size:15px;font-weight:700;color:${C.dark};margin-bottom:10px;">Opens &amp; Clicks Over Time</div>${chart}</td></tr>` : ''}
${tableHtml ? `<tr><td style="padding:24px 32px 0;"><div style="font-size:15px;font-weight:700;color:${C.dark};margin-bottom:10px;">${esc(data.tableTitle ?? 'Campaign Breakdown')}</div>${tableHtml}</td></tr>` : ''}
<tr><td style="padding:30px 32px 20px;"><table width="100%" style="border-top:1px solid ${C.border};"><tr>
<td style="padding-top:16px;font-size:11px;color:${C.gray};line-height:1.5;">This report was generated by One-Email on behalf of ${esc(data.orgName)}.<br/>Click and open data is based on tracking pixels and link redirects. Actual engagement may vary.</td>
<td style="padding-top:16px;text-align:right;font-size:12px;color:${C.gray};font-weight:600;">one-email.com</td>
</tr></table></td></tr>
</table></body></html>`
}
